"use client";

import { useCallback, useEffect, useState } from "react";
import {
  findUserAcknowledgement,
  getUserAcknowledgements,
  hasLegacyAcknowledgement,
  readCachedAcknowledgement,
  setUserAcknowledgement,
  type UserAcknowledgement,
  type UserAcknowledgementDescriptor,
  type UserAcknowledgementStatus,
  writeCachedAcknowledgement,
} from "@/utils/acknowledgements/userAcknowledgements";

interface UseUserAcknowledgementOptions {
  descriptor: UserAcknowledgementDescriptor;
  orgId?: string | null;
  userId?: string | null;
  legacyStorageKeys?: string[];
}

interface UseUserAcknowledgementResult {
  acknowledgement: UserAcknowledgement | null;
  isAcknowledged: boolean;
  isResolved: boolean;
  acknowledge: (
    status: UserAcknowledgementStatus,
    metadata?: Record<string, unknown>
  ) => Promise<UserAcknowledgement | null>;
}

function buildOptimisticAcknowledgement(
  descriptor: UserAcknowledgementDescriptor,
  status: UserAcknowledgementStatus,
  metadata?: Record<string, unknown>
): UserAcknowledgement {
  const now = new Date().toISOString();
  return {
    ...descriptor,
    status,
    firstAcknowledgedAt: now,
    lastAcknowledgedAt: now,
    metadata,
  };
}

export function useUserAcknowledgement({
  descriptor,
  orgId,
  userId,
  legacyStorageKeys = [],
}: UseUserAcknowledgementOptions): UseUserAcknowledgementResult {
  const [acknowledgement, setAcknowledgement] = useState<UserAcknowledgement | null>(null);
  const [isResolved, setIsResolved] = useState(false);
  const legacyStorageKeysSignature = legacyStorageKeys.filter(Boolean).join("\0");

  useEffect(() => {
    if (!orgId || !userId) {
      setAcknowledgement(null);
      setIsResolved(true);
      return;
    }

    let isCancelled = false;
    const stableLegacyStorageKeys = legacyStorageKeysSignature
      ? legacyStorageKeysSignature.split("\0")
      : [];
    const cachedAcknowledgement = readCachedAcknowledgement(descriptor, orgId, userId);
    const legacySeen = hasLegacyAcknowledgement(stableLegacyStorageKeys);
    const fallbackAcknowledgement =
      cachedAcknowledgement ??
      (legacySeen
        ? buildOptimisticAcknowledgement(descriptor, "completed", {
            migratedFrom: "legacy_local_storage",
          })
        : null);

    setAcknowledgement(fallbackAcknowledgement);
    setIsResolved(false);

    const resolveAcknowledgement = async () => {
      try {
        const acknowledgements = await getUserAcknowledgements(userId);
        if (isCancelled) return;

        const serverAcknowledgement = findUserAcknowledgement(acknowledgements, descriptor);
        if (serverAcknowledgement) {
          writeCachedAcknowledgement(descriptor, orgId, userId, serverAcknowledgement);
          setAcknowledgement(serverAcknowledgement);
          setIsResolved(true);
          return;
        }

        if (cachedAcknowledgement || legacySeen) {
          const migratedAcknowledgement = await setUserAcknowledgement(
            userId,
            descriptor,
            cachedAcknowledgement?.status ?? "completed",
            cachedAcknowledgement?.metadata ?? { migratedFrom: "legacy_local_storage" }
          );

          if (isCancelled) return;

          if (migratedAcknowledgement) {
            writeCachedAcknowledgement(descriptor, orgId, userId, migratedAcknowledgement);
            setAcknowledgement(migratedAcknowledgement);
            setIsResolved(true);
            return;
          }
          setAcknowledgement(fallbackAcknowledgement);
          setIsResolved(true);
          return;
        }

        setAcknowledgement(null);
        setIsResolved(true);
      } catch {
        if (!isCancelled) {
          setAcknowledgement(fallbackAcknowledgement);
          setIsResolved(true);
        }
      }
    };

    resolveAcknowledgement();

    return () => {
      isCancelled = true;
    };
  }, [descriptor, legacyStorageKeysSignature, orgId, userId]);

  const acknowledge = useCallback(
    async (status: UserAcknowledgementStatus, metadata?: Record<string, unknown>) => {
      if (!orgId || !userId) return null;

      const previousAcknowledgement = acknowledgement;
      const optimisticAcknowledgement = buildOptimisticAcknowledgement(descriptor, status, metadata);
      setAcknowledgement(optimisticAcknowledgement);
      setIsResolved(true);

      try {
        const persistedAcknowledgement = await setUserAcknowledgement(
          userId,
          descriptor,
          status,
          metadata
        );

        if (persistedAcknowledgement) {
          writeCachedAcknowledgement(descriptor, orgId, userId, persistedAcknowledgement);
          setAcknowledgement(persistedAcknowledgement);
          return persistedAcknowledgement;
        }

        setAcknowledgement(previousAcknowledgement);
        return null;
      } catch {
        setAcknowledgement(previousAcknowledgement);
        return null;
      }
    },
    [acknowledgement, descriptor, orgId, userId]
  );

  return {
    acknowledgement,
    isAcknowledged: acknowledgement !== null,
    isResolved,
    acknowledge,
  };
}
